import { proxyCustomElement, HTMLElement, createEvent, h, Fragment, Host } from '@stencil/core/internal/client';
import { f as forwardEvent } from './index3.js';
import { s as setInputRef, g as getError, a as getValidityObject, c as checkValidity, r as reportValidity, b as setCustomValidity, w as watchError, d as watchWarning, e as getHostAttributes, f as getFormFieldAttributes } from './index7.js';
import { g as getBoxAttributes } from './util.js';
import { d as defineCustomElement$9 } from './box.js';
import { d as defineCustomElement$8 } from './button.js';
import { d as defineCustomElement$7 } from './date-input-calendar.js';
import { d as defineCustomElement$6 } from './form-field.js';
import { d as defineCustomElement$5 } from './icon.js';
import { d as defineCustomElement$4 } from './loader.js';
import { d as defineCustomElement$3 } from './menu-pane.js';
import { d as defineCustomElement$2 } from './tooltip.js';
import { d as defineCustomElement$1 } from './tooltip-label.js';

const dateInputCss = ":host{display:flex;width:100%}:host .box{align-items:center;background-color:var(--jw-theme-form-field-default-bg-color);border:1px solid var(--jw-theme-form-border-regular-default-border-color);border-radius:.25rem;color:var(--jw-theme-form-field-filled-color);display:flex;font-family:Open Sans;font-size:.875rem;font-size:inherit;font-weight:400;font-weight:inherit;gap:.5rem;height:2rem;letter-spacing:-.0004em;line-height:1.25rem;min-width:64px;padding:0 .5rem;transition:border-color .3s ease;width:100%}:host .box[disabled]{background-color:var(--jw-theme-form-field-disabled-bg-color);color:var(--jw-theme-form-field-disabled-color);cursor:not-allowed}:host .box:not([disabled]):hover{border-color:var(--jw-theme-form-border-regular-hover-border-color)}:host .box:not([disabled]):focus,:host .box:not([disabled]):focus-within{border-color:var(--jw-theme-form-border-regular-focus-border-color);outline:var(--jw-theme-effects-focus-default-outline)}:host:focus,:host:focus-visible{outline:none}.inner{display:flex;width:100%}:host([is-empty]) .box{color:var(--jw-theme-form-field-placeholder-color)}:host([error-type=error]) .box{border-color:var(--jw-theme-form-border-error-default-border-color)}:host([error-type=error]:not([disabled]):hover) .box{border-color:var(--jw-theme-form-border-error-hover-border-color)}:host([error-type=warning]) .box{border-color:var(--jw-theme-form-border-warning-default-border-color)}:host([error-type=warning]:not([disabled]):hover) .box{border-color:var(--jw-theme-form-border-warning-hover-border-color)}.addon,::slotted(.addon){align-items:center;background-color:var(--jw-theme-form-addon-default-bg-color);color:var(--jw-theme-form-addon-default-color);cursor:inherit;display:inline-flex;flex:none;height:100%;padding:0 .5rem;white-space:nowrap}:host([addon-pos=start]) .box{padding-left:0}:host([addon-pos=end]) .box{padding-right:0}:host([addon-pos=start]) .addon,:host([addon-pos=start]) ::slotted(.addon){border-bottom-left-radius:.25rem;border-top-left-radius:.25rem}:host([addon-pos=end]) .addon,:host([addon-pos=end]) ::slotted(.addon){border-bottom-right-radius:.25rem;border-top-right-radius:.25rem}.prefix-icon{align-items:center;display:inline-flex}:host .prefix-icon wui-icon{color:var(--jw-theme-form-field-placeholder-color)}:host([disabled]) .prefix-icon wui-icon{color:var(--jw-theme-surface-text-disabled-color)}:host .input::placeholder{color:var(--jw-theme-form-field-placeholder-color)}:host .input::-ms-clear,:host .input::-webkit-clear-button,:host .input::-webkit-inner-spin-button,:host .input::-webkit-outer-spin-button{-webkit-appearance:none;appearance:none;display:none;margin:0}:host .input::-webkit-search-cancel-button,:host .input::-webkit-search-decoration,:host .input::-webkit-search-results-button,:host .input::-webkit-search-results-decoration{-webkit-appearance:none;appearance:none}:host .input::-webkit-calendar-picker-indicator{background:none;display:none}:host .input[type=number]{-webkit-appearance:textfield;appearance:textfield}:host .input::-webkit-inner-spin-button,:host .input::-webkit-outer-spin-button{-webkit-appearance:none;appearance:none;margin:0}:host .input:-webkit-autofill,:host .input:-webkit-autofill:first-line{font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem}:host .input:-webkit-autofill,:host .input:-webkit-autofill:focus,:host .input:-webkit-autofill:hover{box-shadow:0 0 0 1000px var(--jw-theme-form-field-default-bg-color) inset;-webkit-text-fill-color:var(--jw-theme-form-field-filled-color)}:host .input:-webkit-autofill[disabled]{box-shadow:0 0 0 1000px var(--jw-theme-form-field-disabled-bg-color) inset}:host .input[disabled]{cursor:not-allowed}:host .input[disabled]::placeholder{color:var(--jw-theme-surface-text-disabled-color)}:host .input{background:none;border:none;box-shadow:none;color:var(--jw-theme-form-field-filled-color);flex:1;float:left;font-family:Open Sans;font-size:.875rem;font-weight:400;height:100%;letter-spacing:-.0004em;line-height:1.25rem;margin:0;min-width:0;padding:0;width:100%}:host .input:focus,:host .input:focus-visible{outline:none}:host([is-empty]) .input{color:var(--jw-theme-form-field-placeholder-color)}:host([disabled]) .input{color:var(--jw-theme-form-field-disabled-color)}:host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;flex:none;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;position:relative;width:-webkit-fit-content;width:fit-content}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}:host .input::-webkit-datetime-edit{max-width:-webkit-max-content;max-width:max-content}.calendar-container{padding:12px 24px}";
const WuiDateInputStyle0 = dateInputCss;

const WuiDateInput = /*@__PURE__*/ proxyCustomElement(class WuiDateInput extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.setInputRef = setInputRef(this);
        this.dirty = false;
        this.getError = getError(this);
        this.addonPosition = 'start';
        this.setValue = (value = '') => {
            this.value = value;
            this.dirty = true;
            this.checkValidity();
            this.el.dispatchEvent(new window.Event('input', { bubbles: true }));
        };
        this.handleInput = (e) => {
            e.stopPropagation();
            const target = e.target;
            this.setValue(target?.value ?? '');
        };
        this.handleKeyDown = (e) => {
            if (e.key === 'Enter') {
                this.toggleCalendar(false);
            }
        };
        this.handleCalendarInput = (e) => {
            // Stop inner event from bubbling up, we'll dispatch a new one on this element
            e.stopPropagation();
            if (this.calendarOpen) {
                this.toggleCalendar(false);
                // Move focus back to input
                this.inputElm?.focus();
            }
            this.setValue(e.target.value);
        };
        this.handleClick = () => {
            if (this.disabled) {
                return;
            }
            this.toggleCalendar(true);
        };
        this.toggleCalendar = (state = !this.calendarOpen) => {
            if (state === this.calendarOpen) {
                return;
            }
            this.calendarOpen = state;
        };
        this.value = undefined;
        this.required = false;
        this.error = undefined;
        this.warning = undefined;
        this.validate = undefined;
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this);
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.formField = undefined;
        this.disabled = false;
        this.weekStartsOn = 0;
        this.name = undefined;
        this.size = 'm';
        this.rangeStartDate = undefined;
        this.rangeEndDate = undefined;
        this.min = undefined;
        this.max = undefined;
        this.addon = undefined;
        this.calendarOpen = undefined;
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    renderInner() {
        return (h(Fragment, null, h("div", { class: "box", ...getBoxAttributes(this), onClick: this.handleClick }, this.addon && typeof this.addon === 'string' && h("span", { class: "addon" }, this.addon), h("span", { class: "prefix-icon" }, h("wui-icon", { name: "calendar", size: "md" })), h("input", { class: "input", type: "date", ref: this.setInputRef, onKeyDown: this.handleKeyDown, disabled: this.disabled, readOnly: this.disabled, required: !!this.required, onClick: (e) => e.preventDefault(), onInput: this.handleInput, onFocus: (e) => {
                forwardEvent(e, this.el);
            }, onBlur: (e) => {
                forwardEvent(e, this.el);
            }, min: this.min, max: this.max, value: this.value })), !this.disabled && (h("wui-menu-pane", { open: this.calendarOpen, onClickOutside: () => {
                this.toggleCalendar(false);
            }, onOpenChanged: (e) => {
                this.toggleCalendar(e.detail);
            }, stretch: "off", placement: "bottom-start" }, h("div", { class: "calendar-container" }, h("wui-date-input-calendar", { value: this.value, onInput: this.handleCalendarInput, disabled: this.disabled,
            // Pass-through props
            weekStartsOn: this.weekStartsOn, min: this.min, max: this.max, rangeStartDate: this.rangeStartDate, rangeEndDate: this.rangeEndDate }))))));
    }
    render() {
        return (h(Host, { key: 'fded80bd28162abed45f7478aba300e8a210df1b', ...getHostAttributes(this), "addon-pos": this.addon ? 'start' : undefined, "calendar-open": this.calendarOpen }, h("wui-form-field", { key: '3fc5f48a4bae3a3eaf9483fa01ec212187d1cda5', ...getFormFieldAttributes(this) }, this.renderInner())));
    }
    get el() { return this; }
    static get watchers() { return {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }; }
    static get style() { return WuiDateInputStyle0; }
}, [1, "wui-date-input", {
        "value": [1025],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16],
        "reportValidity": [16],
        "setCustomValidity": [16],
        "formField": [16],
        "disabled": [516],
        "weekStartsOn": [2, "week-starts-on"],
        "name": [1],
        "size": [1],
        "rangeStartDate": [1, "range-start-date"],
        "rangeEndDate": [1, "range-end-date"],
        "min": [1],
        "max": [1],
        "addon": [1],
        "customValidation": [32],
        "showValidity": [32],
        "calendarOpen": [32]
    }, undefined, {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-date-input", "wui-box", "wui-button", "wui-date-input-calendar", "wui-form-field", "wui-icon", "wui-loader", "wui-menu-pane", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-date-input":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiDateInput);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-date-input-calendar":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-menu-pane":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}
defineCustomElement();

export { WuiDateInput as W, defineCustomElement as d };

//# sourceMappingURL=date-input.js.map